// Centralised content-policy rules for posting title/description.
// Shared between web FE (employer + admin) and the API so a single source
// of truth decides what counts as a contact-info violation. Detection in
// the API is authoritative — FE checks are UX only.

export const POSTING_TITLE_MAX = 120;
export const POSTING_DESCRIPTION_MAX = 5000;
export const POSTING_TITLE_MIN = 4;
export const POSTING_DESCRIPTION_MIN = 10;

// Patterns that indicate contact info leaking into title/description.
// Tuned for Vietnamese context: phone numbers, email, common chat apps.
// Each entry: { pattern, label } so FE can surface what triggered.
export interface ContactViolation {
  kind: "phone" | "email" | "social" | "url";
  label: string;
  excerpt: string;
}

// VN phone: 9–11 digits, optional country code +84, separators . - space.
// We strip diacritics/letters first and detect runs of digits.
const PHONE_REGEX = /(?<!\d)(?:\+?84|0)(?:[\s.\-_]?\d){8,10}(?!\d)/g;

const EMAIL_REGEX = /[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/g;

// Catches "zalo: ...", "tele 0123", "fb.com/...", "ig: name", "wa.me/..."
const SOCIAL_KEYWORDS = [
  "zalo",
  "telegram",
  "whatsapp",
  "viber",
  "messenger",
  "facebook",
  "instagram",
  "tiktok",
  "wechat",
  "line",
];

const URL_REGEX =
  /\b(?:https?:\/\/|www\.)[^\s]+|(?:[a-z0-9-]+\.)+(?:com|vn|net|org|me|io|app|page)\b/gi;

function normalize(input: string): string {
  return input.toLowerCase();
}

function excerpt(source: string, match: string, ctx = 20): string {
  const idx = source.toLowerCase().indexOf(match.toLowerCase());
  if (idx < 0) return match;
  const start = Math.max(0, idx - ctx);
  const end = Math.min(source.length, idx + match.length + ctx);
  return (
    (start > 0 ? "…" : "") +
    source.slice(start, end) +
    (end < source.length ? "…" : "")
  );
}

/**
 * Detect contact-info patterns in posting title/description.
 * Returns an empty array when content is clean.
 */
export function detectContactViolations(input: string): ContactViolation[] {
  if (!input) return [];
  const violations: ContactViolation[] = [];
  const seen = new Set<string>();

  function push(v: ContactViolation) {
    const key = `${v.kind}:${v.label}`;
    if (seen.has(key)) return;
    seen.add(key);
    violations.push(v);
  }

  // Phone: scan with separators stripped — guards against "0 9 1 2 3 4 5 6 7 8"
  // and "09.12.34.56.78" tricks. We test both raw and digit-only forms.
  const phoneMatches = input.match(PHONE_REGEX);
  if (phoneMatches) {
    for (const m of phoneMatches) {
      push({ kind: "phone", label: m.trim(), excerpt: excerpt(input, m) });
    }
  }
  // Digit-only fallback: catches obfuscated phones written with spaces.
  const digitsOnly = input.replace(/\D+/g, "");
  if (digitsOnly.length >= 9 && /^(?:84|0)\d{8,10}$/.test(digitsOnly)) {
    push({
      kind: "phone",
      label: digitsOnly,
      excerpt: "(số điện thoại bị che dấu)",
    });
  }

  const emailMatches = input.match(EMAIL_REGEX);
  if (emailMatches) {
    for (const m of emailMatches) {
      push({ kind: "email", label: m, excerpt: excerpt(input, m) });
    }
  }

  const lower = normalize(input);
  for (const kw of SOCIAL_KEYWORDS) {
    if (lower.includes(kw)) {
      push({ kind: "social", label: kw, excerpt: excerpt(input, kw) });
    }
  }

  const urlMatches = input.match(URL_REGEX);
  if (urlMatches) {
    for (const m of urlMatches) {
      push({ kind: "url", label: m, excerpt: excerpt(input, m) });
    }
  }

  return violations;
}

export function hasContactViolation(input: string): boolean {
  return detectContactViolations(input).length > 0;
}

export function describeViolations(violations: ContactViolation[]): string {
  if (violations.length === 0) return "";
  const groups: Record<ContactViolation["kind"], string[]> = {
    phone: [],
    email: [],
    social: [],
    url: [],
  };
  for (const v of violations) groups[v.kind].push(v.label);
  const parts: string[] = [];
  if (groups.phone.length)
    parts.push(`số điện thoại (${groups.phone.join(", ")})`);
  if (groups.email.length) parts.push(`email (${groups.email.join(", ")})`);
  if (groups.social.length)
    parts.push(`mạng xã hội (${groups.social.join(", ")})`);
  if (groups.url.length) parts.push(`đường dẫn (${groups.url.join(", ")})`);
  return parts.join(" · ");
}
